import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Card, Row, Col, Typography, DatePicker, Spin } from "antd";
import { SelectInput } from "../../../blueplanitUtils/InputField";
import moment from "moment";
import ReactECharts from "echarts-for-react";
import dayjs from "dayjs";
import { CustomGridTable } from "../../../blueplanitUtils/CustomGridTable";
import { fetchEmployeePerformanceStatsAsync } from "../../../Slices/employeePerformanceSlice";
const { Title, Text } = Typography;
 
 
const getEmployeeSLABreakdown = (dueArr = [], compArr = []) => {
  let success = 0, failed = 0, pending = 0;
 
  const len = Math.max(dueArr.length, compArr.length);
  for (let i = 0; i < len; i++) {
    const due = dueArr[i];
    const comp = compArr[i];
 
    if (!due || !comp) {
      pending++;
      continue;
    }
 
    const dueNum = Number(due);
    const compNum = Number(comp);
 
    if (isNaN(dueNum) || isNaN(compNum)) {
      pending++;
      continue;
    }
 
    if (compNum > dueNum) failed++;
    else success++;
  }
 
  return { success, failed, pending };
};
 
 
const checkDueStatus = (dueArr = [], compArr = []) => {
  if (!Array.isArray(dueArr) || !Array.isArray(compArr)) return "Pending";
 
 
  const hasAnyCompleted = compArr.some(c => c !== null && c !== undefined && c !== "");
  if (!hasAnyCompleted) return "Pending";
 
  let anyFailed = false;
  let anySuccess = false;
 
  const len = Math.max(dueArr.length, compArr.length);
  for (let i = 0; i < len; i++) {
    const due = dueArr[i];
    const comp = compArr[i];
    if (due == null || comp == null) continue;
    const dueNum = Number(due);
    const compNum = Number(comp);
    if (isNaN(dueNum) || isNaN(compNum)) continue;
    if (compNum > dueNum) anyFailed = true;
    else anySuccess = true;
  }
 
  if (anyFailed) return "Failed";
  if (anySuccess) return "Success";
  return "Pending";
};
 
const formatHours = (hours) => {
  if (!hours || isNaN(hours)) return "0 sec";
 
  const totalSeconds = hours * 3600;
  const hrs = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  const secs = Math.floor(totalSeconds % 60);
 
  if (hrs >= 1) {
    return `${hrs} hr${hrs > 1 ? "s" : ""} ${mins > 0 ? mins + " min " : ""}${secs > 0 ? secs + " sec" : ""}`.trim();
  }
 
  if (mins >= 1) {
    return `${mins} min${mins > 1 ? "s" : ""} ${secs > 0 ? secs + " sec" : ""}`.trim();
  }
 
  return `${secs} sec`;
};
 
 
 
const performanceColumns = [
  { title: "Employee Name", dataIndex: "employeeName" },
  { title: "Client", dataIndex: "clientName" },
  { title: "Total Hours", dataIndex: "totalHours", render: (v) => formatHours(v) },
  { title: "Emails", dataIndex: "emails", render: (v) => formatHours(v) },
  { title: "Processing", dataIndex: "processing", render: (v) => formatHours(v) },
  { title: "Productive Hours", dataIndex: "totalProductiveHours", render: (v) => formatHours(v) },
  { title: "Non-Productive Hours", dataIndex: "nonProductiveHours", render: (v) => formatHours(v) },
 
  { title: "Requests", dataIndex: "totalRequests" },
 
  /** 🔥 NEW SLA BREAKDOWN COLUMNS */
  { title: "SLA Success", dataIndex: "SLA_success" },
  { title: "SLA Failed", dataIndex: "SLA_failed" },
  { title: "SLA Pending", dataIndex: "SLA_pending" },
];
 
 
 
const EmployeePerformanceClientView = () => {
  const dispatch = useDispatch();
 
  const clients = useSelector((state) => state.clients?.clients || []);
  const { performanceData = [], loading, error } = useSelector(
    (state) => state.employeePerformance || {}
  );
  const allEmployees = useSelector((state) => state.employee?.AllEmployees || []);
 
  const [dateRange, setDateRange] = useState([
    dayjs().startOf("month"),
    dayjs().endOf("month"),
  ]);
  const [selectedClient, setSelectedClient] = useState(null);
 
  const [fromDate, toDate] = dateRange;
 
  /** Fetch once (global performance data) */
  useEffect(() => {
    if (fromDate && toDate) {
      dispatch(
        fetchEmployeePerformanceStatsAsync({
          startDate: fromDate.toISOString(),
          endDate: toDate.toISOString(),
        })
      );
 
    }
  }, [fromDate, toDate]);
 
  /** Group By Client */
  const groupedByClient = useMemo(() => {
    const map = {};
    performanceData.forEach((perf) => {
      const cname = perf.clientName || "Unknown";
      if (!map[cname]) map[cname] = [];
      map[cname].push(perf);
    });
    return map;
  }, [performanceData]);
 
  /** Merge employees + performance */
  const getClientEmployeeData = (clientName) => {
    const performance = groupedByClient[clientName] || [];
 
    const employeesForClient = allEmployees.filter((emp) =>
      emp.client?.some(
        (c) =>
          c?.NameOfClient?.trim()?.toLowerCase() ===
          clientName.trim().toLowerCase()
      )
    );
 
    return employeesForClient.map((emp) => {
      const fullName = `${emp.firstname} ${emp.lastname}`.trim();
 
      const perf = performance.find(
        (p) => p.employeeName?.toLowerCase() === fullName.toLowerCase()
      );
 
      const slaBreak = getEmployeeSLABreakdown(
        perf?.dueDate || [],
        perf?.completedDate || []
      );
 
      return {
        employeeName: fullName,
        clientName,
        totalHours: Number(perf?.totalHours || 0),
        totalRequests: Number(perf?.totalRequests || 0),
        emails: Number(perf?.productive?.emails || 0),
        processing: Number(perf?.productive?.processing || 0),
        quality: 0,
        totalProductiveHours:
          Number(perf?.productive?.emails || 0) +
          Number(perf?.productive?.processing || 0),
        nonProductiveHours: Number(perf?.nonProductiveHours || 0),
        dueDate: perf?.dueDate || [],
        completedDate: perf?.completedDate || [],
 
        /** 🔥 This is the SLA per employee breakdown */
        SLA_success: slaBreak.success,
        SLA_failed: slaBreak.failed,
        SLA_pending: slaBreak.pending,
      };
 
 
    });
  };
 
  return (
    <div className="page-wrapper">
      <div className="content container-fluid">

        {/* Header – SAME DESIGN */}
        <Row justify="space-between" align="middle" style={{ marginBottom: 30 }}>
          <Col>
            <Title level={3}>Employee Performance – Client View</Title>
            <Text type="secondary">
              Select a client to view performance
            </Text>
          </Col>

          <Col style={{ display: "flex", gap: 10 }}>
            <SelectInput
              allowClear
              showSearch
              placeholder="Select Client Name"
              style={{ width: 220 }}
              value={selectedClient}
              onChange={(v) => setSelectedClient(v)}
              options={clients.map((c) => ({
                label: c.clientName,
                value: c.clientName,
              }))}
            />

            <DatePicker
              style={{ width: 120, height: 32 }}
              value={fromDate}
              onChange={(date) => setDateRange([date.startOf("day"), toDate])}
              format="DD/MM/YYYY"
              placeholder="From Date"
            />

            {/* To Date */}
            <DatePicker
              style={{ width: 120, height: 32 }}
              value={toDate}
              onChange={(date) => setDateRange([fromDate, date.endOf("day")])}
              format="DD/MM/YYYY"
              placeholder="To Date"
            />
          </Col>
        </Row>

        {error && <p style={{ color: "red", textAlign: "center" }}>{error}</p>}

        {/* Default placeholder text */}
        {!selectedClient && !loading && (
          <p style={{ textAlign: "center", marginTop: 50, fontSize: 16 }}>
            Please select a <strong>Client</strong>.
          </p>
        )}

        {loading && (
          <div style={{ textAlign: "center", padding: 60 }}>
            <Spin size="large" />
            <p style={{ marginTop: 12 }}>Loading performance data...</p>
          </div>
        )}
 
        {/* Selected client card */}
        {selectedClient && !loading && (
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Card
                title={
                  <div style={{ textAlign: "center" }}>
                    <Title level={4} style={{ margin: 0 }}>
                      {selectedClient}
                    </Title>
                  </div>
                }
                style={{ borderRadius: 12, minHeight: 480 }}
              >
                {(() => {
                  const data = getClientEmployeeData(selectedClient);
 
                  const totalHours = data.reduce((s, c) => s + c.totalHours, 0);
                  const productiveTotal = data.reduce(
                    (s, c) => s + c.totalProductiveHours,
                    0
                  );
                  const nonProductiveTotal = data.reduce(
                    (s, c) => s + c.nonProductiveHours,
                    0
                  );
 
                  const slaSummary = {
                    Success: data.reduce((sum, c) => sum + (c.SLA_success || 0), 0),
                    Failed: data.reduce((sum, c) => sum + (c.SLA_failed || 0), 0),
                    Pending: data.reduce((sum, c) => sum + (c.SLA_pending || 0), 0),
                  };
 
 
                  const chartOption = {
                    title: { text: "Hours by Employee", left: "center" },
                    tooltip: { trigger: "axis" },
                    legend: {
                      top: 30,
                      data: ["Emails", "Processing", "Non-Productive"],
                    },
 
                    xAxis: {
                      type: "category",
                      data: data.map((d) => d.employeeName),
                      axisLabel: { rotate: 20 },
                    },
                    yAxis: { type: "value", name: "Hours" },
                    series: [
                      {
                        name: "Emails",
                        type: "bar",
                        stack: "hours",
                        data: data.map((d) => d.emails),
                      },
                      {
                        name: "Processing",
                        type: "bar",
                        stack: "hours",
                        data: data.map((d) => d.processing),
                      },
                      {
                        name: "Non-Productive",
                        type: "bar",
                        stack: "hours",
                        data: data.map((d) => d.nonProductiveHours),
                      },
                    ],
                  };
 
                  return (
                    <>
                      <ReactECharts option={chartOption} style={{ height: 360 }} />
 
                      <div
                        style={{
                          marginTop: 15,
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <p>
                          <strong>Total Hours:</strong> {formatHours(totalHours)}
                        </p>
                        <p>
                          <strong>Productive:</strong> {formatHours(productiveTotal)}
                        </p>
                        <p>
                          <strong>Non-Productive:</strong> {formatHours(nonProductiveTotal)}
                        </p>
                        <p><strong>SLA Success:</strong> {slaSummary.Success}</p>
                        <p><strong>SLA Failed:</strong> {slaSummary.Failed}</p>
                        <p><strong>SLA Pending:</strong> {slaSummary.Pending}</p>
                      </div>
 
                      <div style={{ marginTop: 20 }}>
                        <CustomGridTable
                          Id="client-view-table"
                          columns={performanceColumns}
                          dataSource={data}
                          pagination={false}
                        />
                      </div>
                    </>
                  );
                })()}
              </Card>
            </Col>
          </Row>
        )}
      </div>
    </div>
  );
};
 
export default EmployeePerformanceClientView;